import { MediaMatcher } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, Inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { ENVIRONMENT_CONFIG, SystemInfoComponent } from '@cargos/sprintpay-ui';
import { Subject, filter, takeUntil } from 'rxjs';
import { ThemeType } from 'src/app/shared/models';
import { SanitizerPipe } from 'src/app/shared/pipes';
import { ConfigService, CurrentFacilityService, ThemeService } from 'src/app/shared/services';
import { CustomerServiceInfo } from 'src/app/shared/services/current-facility.service';
import { environment } from 'src/environments/environment';
import { CustomerServiceComponent } from '../../customer-service/customer-service.component';
import { ThemeListType } from './models/theme-types';
import { SettingsMaterialModule } from './settings-material-modules.module';

@Component({
    standalone: true,
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        SettingsMaterialModule,
        ReactiveFormsModule,
        SanitizerPipe,
        CustomerServiceComponent,
        SystemInfoComponent,
    ],
    providers: [
        {
            provide: ENVIRONMENT_CONFIG,
            useValue: environment,
        },
    ],
})
export class SettingsComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    readonly themeAnchor = this.document.getElementById('appTheme');
    public mobileQuery: MediaQueryList;
    public initialTheme: FormControl = new FormControl<ThemeType>(this.themeService.instant_theme);
    public listTheme: ThemeListType[] = [
        {
            name: 'Light theme',
            id: 'light',
            checked: false,
        },
        {
            name: 'Dark theme',
            id: 'dark',
            checked: false,
        },
    ];
    public showSettings = false;
    public termsOfUseURL = '';
    public privacyAndPolicyURL = '';
    public customerServiceInfo: CustomerServiceInfo | null = null;
    public termsOfUseExternal: boolean;
    private prevTheme: ThemeType;
    private currentRoute: string = '';
    public isDarkModeEnable = true;
    @Input() settingsButtonOutline: boolean = false;

    constructor(
        private themeService: ThemeService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
        private media: MediaMatcher,
        private currentFacilityService: CurrentFacilityService,
        private router: Router,
        private configService: ConfigService
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.prevTheme = this.themeService.instant_theme;
        this.mobileQuery = this.media.matchMedia('(max-width: 576px)');
    }

    ngOnInit(): void {
        this.subscribeUrl();
        this.getTermsOfUseUrl();
        this.getPrivacyAndPolicyUrl();
        this.getCustomerServiceInfo();
        this.subscribeTheme();
        this.isTermsOfUseExternal();
        this.setTheme();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    setTheme(): void {
        this.isDarkModeEnable = this.configService.isDarkModeEnable();
        if (this.isDarkModeEnable) {
            if (this.initialTheme.value === ThemeType.dark) {
                this.listTheme[1].checked = true;
            } else {
                this.listTheme[0].checked = true;
            }
            this.radioChange(this.initialTheme.value);
        }
    }

    radioChange(theme: ThemeType): void {
        this.initialTheme.setValue(theme);
        this.themeService.setTheme(theme);

        if (!(theme === ThemeType.dark)) {
            this.renderer.setAttribute(this.themeAnchor, 'href', '/light-theme.css');
        } else {
            this.renderer.setAttribute(this.themeAnchor, 'href', '/dark-theme.css');
        }
    }

    getTermsOfUseUrl(): void {
        this.termsOfUseURL = this.currentFacilityService.getTermsOfUseURL();
    }

    getPrivacyAndPolicyUrl(): void {
        this.privacyAndPolicyURL = this.currentFacilityService.getPrivacyAndPolicyURL();
    }

    getCustomerServiceInfo(): void {
        this.customerServiceInfo = this.currentFacilityService.getCustomerServiceInfo();
    }

    subscribeTheme(): void {
        this.themeService
            .getTheme()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (theme) => {
                    if (theme !== this.prevTheme && this.currentRoute.includes('facility-information')) {
                        window.location.reload();
                    }
                },
            });
    }

    subscribeUrl(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((event) => {
                this.currentRoute = event['url'];
            });
    }

    navigateToTermsOfUse(): void {
        this.router.navigate(['/termsofuse']);
    }

    isTermsOfUseExternal(): void {
        this.termsOfUseExternal = this.configService.isTermsOfUseExternal();
    }
}
